import { RenderElse, RenderIf } from "components/render-ifelse/render-ifelse";
import illusUpgradeError from "images/illus_upgrade_error.svg";
import illusWarning from "images/illus_warning.png";
import NotFound from "pages/not-found";
import Success from "pages/success";
import { Trans, useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { zoomState } from "state";
import ZJSBridge from "zalo-js-bridge";
import { ECode } from "ztypes/error-code";
import { QuotaUpgrade } from "ztypes/zba";

interface InvalidQuotaData {
  zba: boolean | false;
  communityCount: number | 0;
  communityQuota: number | 0;
}

interface IProps {
  ecode: ECode;
  data?: InvalidQuotaData;
}

const ErrorPage: React.FC<IProps> = (props: IProps) => {
  const { t } = useTranslation();
  const zoom = useRecoilValue(zoomState);
  const error = props.ecode;
  const data = props.data;

  const closeWebview = () => {
    ZJSBridge.H5.closeWebview();
  };

  if (error == ECode.SUCCESS) {
    return <Success />;
  }

  if (
    error === ECode.EXCEPTION ||
    (error == ECode.INVALID_QUOTA && !data) ||
    (error == ECode.INVALID_QUOTA &&
      data &&
      data.zba &&
      data.communityCount < data.communityQuota)
  ) {
    return <NotFound />;
  }

  const numberDelete = data?.zba
    ? data.communityCount - data.communityQuota + 1
    : 0;
  return (
    <div className="pt-20 short:pt-10 px-6 pb-6 flex flex-col h-screen text-size-adjust-auto">
      <div
        className="flex flex-col items-center flex-grow self-stretch"
        style={{ zoom: zoom }}
      >
        <div className="flex flex-col items-center self-stretch">
          <img className="w-32 h-32" src={illusUpgradeError} alt="" />
        </div>
        <div className="mt-6 flex flex-col items-center">
          {error == ECode.INVALID_QUOTA && data != undefined && (
            <>
              <RenderIf isTrue={data.zba}>
                <RenderIf isTrue={data.communityCount >= data.communityQuota}>
                  <div className="text-center font-medium text-[18px] leading-[24px]">
                    {t("error.upgrade_community.title")}
                  </div>
                  <div className="mt-4 text-center font-normal text-[14px] leading-[18px] text-neutral-500">
                    <Trans
                      i18nKey={`error.upgrade_community.need_delete.text_1${
                        data.communityQuota == 1 ? "" : "_plural"
                      }`}
                      values={{ count: data.communityQuota }}
                      components={{
                        font500: <span className="font-medium"></span>,
                      }}
                    />
                  </div>
                  <div className="mt-4 text-center font-normal text-[14px] leading-[18px] text-neutral-500">
                    <Trans
                      i18nKey={`error.upgrade_community.need_delete.text_2${
                        numberDelete == 1 ? "" : "_plural"
                      }`}
                      values={{ count: numberDelete }}
                      components={{
                        font500: <span className="font-medium"></span>,
                      }}
                    />
                  </div>
                </RenderIf>
              </RenderIf>
              <RenderElse isTrue={data.zba}>
                <RenderIf isTrue={data.communityCount < QuotaUpgrade.ZBA_PRO}>
                  <div className="text-center font-normal text-[16px] leading-[22px] text-neutral-500">
                    {t("error.upgrade_community.not_eligible.text_1")}
                  </div>
                </RenderIf>
                <RenderIf isTrue={data.communityCount >= QuotaUpgrade.ZBA_PRO}>
                  <div className="text-center font-medium text-[18px] leading-[24px]">
                    {t("error.upgrade_community.title")}
                  </div>
                  <div className="mt-4 text-center font-normal text-[14px] leading-[18px] text-neutral-500">
                    {t("error.upgrade_community.reach_maximum_quota.text_1")}
                  </div>
                </RenderIf>
              </RenderElse>
            </>
          )}
          <RenderIf isTrue={error == ECode.NOT_GROUP_OWNER}>
            <div className="text-center font-normal text-[16px] leading-[22px] text-neutral-500">
              {t("error.upgrade_community.not_group_owner.title")}
            </div>
          </RenderIf>
          <RenderIf isTrue={error == ECode.NOT_SUPPORT_VERSION}>
            <>
              <div className="text-center font-medium text-[18px] leading-[24px]">
                {t("error.upgrade_community.header.title")}
              </div>
              <div className="mt-4 text-center font-normal text-[14px] leading-[18px] text-neutral-500">
                {t("error.upgrade_community.not_support_version.title")}
              </div>
            </>
          </RenderIf>
          <RenderIf isTrue={error == ECode.NEW_ACCOUNT}>
            <div className="text-center font-normal text-[16px] leading-[22px] text-neutral-500">
              {t("error.regis_zba.account_30days.title")}
            </div>
          </RenderIf>
          <RenderIf isTrue={error == ECode.MIN_AGE_EXCEED}>
            <div className="text-center font-normal text-[16px] leading-[22px] text-neutral-500">
              {t("error.regis_zba.min_age_exceed.title")}
            </div>
          </RenderIf>
          <RenderIf isTrue={error == ECode.FOREIGN_PHONE}>
            <div className="text-center font-normal text-[16px] leading-[22px] text-neutral-500">
              {t("error.regis_zba.foreign_phone.title")}
            </div>
          </RenderIf>
          <RenderIf isTrue={error == ECode.ALREADY_ZBA}>
            <>
              <div className="text-center font-medium text-[18px] leading-[24px]">
                {t("error.regis_zba.already_zba.title")}
              </div>
              <div className="mt-4 text-center font-normal text-[14px] leading-[18px] text-neutral-500">
                {t("error.regis_zba.already_zba.sub_title")}
              </div>
            </>
          </RenderIf>
          <RenderIf isTrue={error == ECode.SUBSCRIPTION_CONFLICT}>
            <>
              <div className="text-center font-medium text-[18px] leading-[24px]">
                {t("error.regis_zba.subscription_conflict.title")}
              </div>
              <div className="mt-4 text-center font-normal text-[14px] leading-[18px] text-neutral-500">
                {t("error.regis_zba.subscription_conflict.sub_title")}
              </div>
            </>
          </RenderIf>
          <RenderIf isTrue={error == ECode.HAS_BOT}>
            <div className="text-center font-medium text-[18px] leading-[24px]">
              {t("error.upgrade_community.title")}
            </div>
            <div className="mt-4 text-center font-normal text-[14px] leading-[18px] text-neutral-500">
              {t("error.upgrade_community.has_bot.text_1")}
            </div>
            <div className="mt-4 text-center font-normal text-[14px] leading-[18px] text-neutral-500">
              {t("error.upgrade_community.has_bot.text_2")}
            </div>
          </RenderIf>
          <RenderIf
            isTrue={
              error == ECode.BLACK_USER ||
              error == ECode.CLONE_USER ||
              error == ECode.USER_NOT_KYC ||
              error == ECode.FAIL_FIELD44 ||
              error == ECode.INVALID_STATUS ||
              error == ECode.NOT_ENOUGH_FRIEND
            }
          >
            <div className="text-center font-normal text-[16px] leading-[22px] text-neutral-500">
              {t("error.regis_zba.not_qualified.title")}
            </div>
          </RenderIf>
        </div>
      </div>
      <RenderIf isTrue={error == ECode.INVALID_QUOTA}>
        <div style={{ zoom: zoom }}>
          <a
            className="flex flex-col h-10 px-6 py-3 justify-center items-center self-stretch bg-blue-600 rounded-full"
            href="https://help.zalo.me/huong-dan/chuyen-muc/tro-chuyen-nhom/lam-gi-khi-khong-the-nang-cap-nhom-len-cong-dong/"
          >
            <div className="flex items-center font-medium text-[15px] leading-[20px] text-white">
              {t("button.learn_more")}
            </div>
          </a>
          <div
            className="flex flex-col h-10 px-6 py-3 justify-center items-center self-stretch"
            onClick={closeWebview}
          >
            <div className="flex items-center font-medium text-[15px] leading-[20px]">
              {t("button.close")}
            </div>
          </div>
        </div>
      </RenderIf>
    </div>
  );
};

export default ErrorPage;
